import { Cart } from "@/components/Icons/utility/Cart.tsx";
import { Loader } from "@/components/Loader/Loader.tsx";
import { MobileCartBarProps } from "@/components/MobileCartBar/MobileCartBarProps.ts";
import { AvailabilityPopoverContent } from "@/components/Popover/AvailabilityPopoverContent.tsx";
import { useReservationSidebarData } from "@/components/ReservationSidebar/useReservationSidebarData.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { PaymentProviderEnum } from "@/helpers/types/types.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import { useCheckCartAccommodationAvailibility } from "@/pages/AccommodationPicking/hooks/useCheckCartAccommodationAvailibility.ts";
import { useIsFormValid } from "@/pages/BookingEngine/hooks/useIsFormValid.ts";
import { useMobileDrawer } from "@/pages/BookingEngine/hooks/useMobileDrawer.ts";
import { useValidateExtraOptionsStep } from "@/pages/ExtraOptions/hooks/useValidateExtraOptionsStep.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useValidFormStore } from "@/stores/ValidFormStore/ValidFormStore.ts";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "react-tiny-popover";

export const MobileCartBar = ({
  holderFormData,
  paymentFormData,
  reservationOfferLoading,
}: MobileCartBarProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    cartAccommodationItems,
    calculateCartLoading,
    calculatedCartData,
    defaultDateRange,
    page,
    isCartItemInDisabledRateGroupFromRootAttributes,
    isForcedInquiry,
  } = useReservationSidebarData(!isMobile);
  const { paymentEnabled, paymentType } = useGetOrganization();
  const availibilityDisabled = useCheckCartAccommodationAvailibility();
  const { areParcelVehicleDimensionsMissing, isCaravanMoverMissing } = useValidateExtraOptionsStep();

  const shouldShowPaymentForm = useMemo(
    () => !isForcedInquiry && paymentEnabled && paymentType === PaymentProviderEnum.WsPayPer,
    [isForcedInquiry, paymentEnabled, paymentType],
  );

  const { isFullFormValid, isHolderFormValid } = useIsFormValid(holderFormData, paymentFormData);
  const { handleSubmit, reset: resetHolderForm } = holderFormData;
  const { handleSubmitPaymentForm, reset: resetPaymentForm, onSubmitPaymentForm } = paymentFormData;

  const { handleOpenCartDrawer, handleCloseCartDrawer, handleMobileContinue, getMobileBtnText } =
    useMobileDrawer(handleSubmit, handleSubmitPaymentForm(onSubmitPaymentForm), () => {
      resetPaymentForm();
      resetHolderForm();
    });
  const isValid = useValidFormStore((state) => state.isValid);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const disableMobileContinue =
    // if no cartItems
    !cartAccommodationItems.length ||
    // if no dateRange
    !(defaultDateRange?.[0] && defaultDateRange?.[1]) ||
    (page === AppPage.ExtraOptions && (areParcelVehicleDimensionsMissing || isCaravanMoverMissing)) ||
    // if on form page & invalid form
    (page === AppPage.HolderAndPaymentInfo &&
      (shouldShowPaymentForm ? !isFullFormValid : !isHolderFormValid)) ||
    availibilityDisabled ||
    !isValid ||
    isCartItemInDisabledRateGroupFromRootAttributes ||
    !!calculatedCartData?.error;

  let totalPrice: string | number = 0;
  if (calculatedCartData?.priceBreakdown?.withDiscount)
    totalPrice = formatCurrency(calculatedCartData.priceBreakdown?.withDiscount);
  if (calculatedCartData?.error) totalPrice = calculatedCartData?.error || t("error");

  if (page === AppPage.ReservationOverview) return null;
  if (reservationOfferLoading) return null;

  return (
    <>
      <i className="o-booking-engine__mobile-cart-overlay" onClick={handleCloseCartDrawer}></i>
      <div
        className={`o-booking-engine__mobile-cart-bar ${cartAccommodationItems.length ? "is-visible" : ""}`}
      >
        <div className="o-booking-engine__mobile-cart-bar-price">
          <div className="c-reservation-price">
            <div className="c-reservation-price__total">
              <p className="u-p1 u-fw-600 c-reservation-price__total-label">{t("total")}: </p>
              <div
                className={`u-h5 u-fw-600 c-reservation-price__total-price ${
                  calculatedCartData?.error ? "c-reservation-sidebar__total-error-price" : undefined
                }`}
              >
                {calculateCartLoading ? <Loader small loading /> : totalPrice}
              </div>
            </div>
          </div>
        </div>
        <div className="o-booking-engine__mobile-cart-bar-actions">
          <Popover isOpen={popoverOpen} content={AvailabilityPopoverContent}>
            <button
              className="c-be-button c-be-button--secondary"
              onClick={handleMobileContinue}
              disabled={disableMobileContinue}
              onMouseOver={() => availibilityDisabled && setPopoverOpen(true)}
              onMouseLeave={() => availibilityDisabled && setPopoverOpen(false)}
            >
              <span className="c-be-button__label">{getMobileBtnText()}</span>
            </button>
          </Popover>
          <button
            className="c-be-button"
            disabled={!cartAccommodationItems.length}
            onClick={handleOpenCartDrawer}
          >
            <span className="c-be-button__icon">
              <Cart />
            </span>
            <span className="c-be-button__cartQuantity">{cartAccommodationItems?.length}</span>
          </button>
        </div>
      </div>
    </>
  );
};
